import { useEffect, useState } from "react";
import { Upload, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import ModulesEditor from "@/pages/admin/ModulesEditor";

export const Uploader = ({ onDone, accept, testid }) => {
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      onDone(data.url, data.filename);
      toast.success("Uploaded");
    } catch (er) { toast.error(errMsg(er)); }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <label className="btn-outline !py-2 !px-4 cursor-pointer shrink-0" data-testid={testid}>
      <Upload size={14} />{busy ? "Uploading..." : "Upload"}
      <input type="file" accept={accept} className="hidden" onChange={up} />
    </label>
  );
};

const CategorySelect = ({ value, onChange, id }) => {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/admin/content/categories").then((r) => setCats(r.data)); }, []);
  return (
    <select id={id} value={value || ""} onChange={(e) => onChange(e.target.value)} className="field" data-testid={`field-${id}`}>
      <option value="">Choose...</option>
      {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
    </select>
  );
};

export const Attachments = ({ value = [], onChange, id }) => (
  <div className="space-y-2" data-testid={`field-${id}`}>
    {value.map((a, i) => (
      <div key={i} className="flex gap-2">
        <input value={a.title} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} placeholder="Title" className="field !py-2" />
        <input value={a.url} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))} placeholder="URL" className="field !py-2" />
        <Uploader onDone={(url) => onChange(value.map((x, j) => (j === i ? { ...x, url } : x)))} />
        <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-400 px-2" aria-label="Remove"><Trash2 size={15} /></button>
      </div>
    ))}
    <button type="button" onClick={() => onChange([...value, { title: "", url: "" }])} className="text-sm text-gold-light flex items-center gap-1"><Plus size={14} />Add resource</button>
  </div>
);

export function FieldInput({ f, value, onChange }) {
  const id = f.k;
  switch (f.type) {
    case "textarea":
      return <textarea id={id} rows={f.rows || 4} value={value || ""} onChange={(e) => onChange(e.target.value)} className="field" data-testid={`field-${id}`} />;
    case "number":
      return <input id={id} type="number" step="any" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className="field" data-testid={`field-${id}`} />;
    case "date":
      return <input id={id} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="field" data-testid={`field-${id}`} />;
    case "bool":
      return <Switch id={id} checked={!!value} onCheckedChange={onChange} data-testid={`field-${id}`} />;
    case "select":
      return (
        <select id={id} value={value || ""} onChange={(e) => onChange(e.target.value)} className="field" data-testid={`field-${id}`}>
          <option value="">Choose...</option>
          {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      );
    case "category":
      return <CategorySelect id={id} value={value} onChange={onChange} />;
    case "image":
    case "file":
      return (
        <div className="flex gap-3 items-center">
          {f.type === "image" && value && <img src={fileUrl(value)} alt="" className="w-16 h-12 object-cover rounded" />}
          <input id={id} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL or upload" className="field" data-testid={`field-${id}`} />
          <Uploader onDone={onChange} accept={f.type === "image" ? "image/*" : undefined} testid={`upload-${id}`} />
        </div>
      );
    case "lines":
      return <textarea id={id} rows={4} value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} className="field" data-testid={`field-${id}`} />;
    case "attachments":
      return <Attachments id={id} value={value} onChange={onChange} />;
    case "modules":
      return <ModulesEditor value={value || []} onChange={onChange} />;
    default:
      return <input id={id} value={value || ""} required={f.required} onChange={(e) => onChange(e.target.value)} className="field" data-testid={`field-${id}`} />;
  }
}
